import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  ShadcnSelect,
  ShadcnSelectContent,
  ShadcnSelectItem,
  ShadcnSelectTrigger,
  ShadcnSelectValue,
  useAuth,
} from '@aaas/shared';
import { Check, Copy, Link, Loader2, Share2 } from 'lucide-react';
import shareService from '../services/share.service';
import type { ShareLinkResponse } from '../types';

const EXPIRE_OPTIONS_KEYS = [
  { value: 1, key: '1h' },
  { value: 3, key: '3h' },
  { value: 12, key: '12h' },
  { value: 24, key: '1d' },
  { value: 72, key: '3d' },
  { value: 168, key: '7d' },
  { value: 336, key: '14d' },
  { value: 720, key: '30d' },
] as const;

interface ShareCVsDialogProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  selectedCVIds: string[];
}

export function ShareCVsDialog({ open, onClose, jobId, selectedCVIds }: ShareCVsDialogProps) {
  const { user, currentWorkspace } = useAuth();
  const { t, i18n } = useTranslation('ats');

  const [isLoading, setIsLoading] = useState(false);
  const [shareLink, setShareLink] = useState<ShareLinkResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hideContactInfo, setHideContactInfo] = useState(true);
  const [expiresInHours, setExpiresInHours] = useState(168);

  const handleCreateShareLink = useCallback(async () => {
    if (!user || !currentWorkspace) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = await user.getIdToken();
      const result = await shareService.createShareLink(jobId, currentWorkspace.id, token, {
        cv_ids: selectedCVIds,
        expires_in_hours: expiresInHours,
        hide_contact_info: hideContactInfo,
      });
      setShareLink(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('shareCvs.createFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [user, currentWorkspace, jobId, selectedCVIds, hideContactInfo, expiresInHours, t]);

  const handleCopyLink = useCallback(() => {
    if (!shareLink) return;

    navigator.clipboard.writeText(shareLink.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [shareLink]);

  const handleClose = useCallback(() => {
    setShareLink(null);
    setCopied(false);
    setError(null);
    onClose();
  }, [onClose]);

  const formatExpiresAt = (dateStr: string) => {
    const date = new Date(dateStr);
    const locale = i18n.language === 'vi' ? 'vi-VN' : 'en-US';
    return date.toLocaleString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-blue-500" />
            {t('shareCvs.title')}
          </DialogTitle>
          <DialogDescription>
            {t('shareCvs.description', { count: selectedCVIds.length })}
          </DialogDescription>
        </DialogHeader>

        {!shareLink ? (
          // Initial state - show create button
          <div className="space-y-4 py-4">
            <div className="bg-muted p-4 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{t('shareCvs.expiresIn')}</span>
                <ShadcnSelect
                  value={String(expiresInHours)}
                  onValueChange={(v) => setExpiresInHours(Number(v))}
                >
                  <ShadcnSelectTrigger className="w-[130px] h-8 bg-background">
                    <ShadcnSelectValue />
                  </ShadcnSelectTrigger>
                  <ShadcnSelectContent>
                    {EXPIRE_OPTIONS_KEYS.map((opt) => (
                      <ShadcnSelectItem key={opt.value} value={String(opt.value)}>
                        {t(`shareCvs.expireOptions.${opt.key}`)}
                      </ShadcnSelectItem>
                    ))}
                  </ShadcnSelectContent>
                </ShadcnSelect>
              </div>
              <p className="text-sm text-muted-foreground">
                {t('shareCvs.viewerNote')}
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="hide-contact-info"
                checked={hideContactInfo}
                onCheckedChange={(checked) => setHideContactInfo(checked === true)}
              />
              <label htmlFor="hide-contact-info" className="text-sm cursor-pointer select-none">
                {t('shareCvs.hideContact')}
              </label>
            </div>

            {error && (
              <div className="bg-destructive/10 text-destructive p-3 rounded-lg text-sm">
                {error}
              </div>
            )}
          </div>
        ) : (
          // After link created - show the link
          <div className="space-y-4 py-4">
            <div className="flex items-center space-x-2">
              <div className="grid flex-1 gap-2">
                <Input readOnly value={shareLink.url} className="font-mono text-sm" />
              </div>
              <Button type="button" size="sm" className="px-3" onClick={handleCopyLink}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>

            <div className="bg-muted p-4 rounded-lg space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Link className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">{t('shareCvs.cvsShared', { count: shareLink.cv_count })}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {t('shareCvs.expires')} <strong>{formatExpiresAt(shareLink.expires_at)}</strong>
              </p>
              {shareLink.hide_contact_info && (
                <p className="text-sm text-muted-foreground">
                  {t('shareCvs.contactHidden')}
                </p>
              )}
            </div>

            {copied && (
              <p className="text-sm text-green-600 text-center">{t('shareCvs.copied')}</p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t('shareCvs.close')}
          </Button>
          {!shareLink && (
            <Button onClick={handleCreateShareLink} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('shareCvs.creating')}
                </>
              ) : (
                <>
                  <Link className="h-4 w-4 mr-2" />
                  {t('shareCvs.createLink')}
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
